import React, { useContext, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import MessageModal from "../../../../../customComponents/MessageModal";
import { Button } from "../../../../../stories/Button/Button";
import { Form, Formik, FormikProps } from "formik";
import { IObjectKeys } from "../../../../../utils/formTypes";
import {
  LicenseTypes,
  Operation,
  PageFor,
} from "../../../../../utils/Enum.types";
import { useNavigate } from "react-router-dom";
import { emptyMessage } from "../../../../../utils/constants";

import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import { AppContext } from "../../../../../context/context";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { useMutation } from "@apollo/client";
import Close from "../../../../../images/Close.svg";
import { emailConfigValidation } from "../../../../../utils/validationSchema";
import Add from "./Add";
import List from "./List";
import {
  AddEmailConfigResult,
  AddEmailConfigVar,
  UpdateEmailConfigResult,
  UpdateEmailConfigVar,
} from "./queries/types";
import { AddEmailConfig, UpdateEmailConfig } from "./queries/mutations";
import { GetEmailConfigs } from "./queries/queries";
import LoadingModal from "../../../../pages/LoadingModal";
import Navbar from "../../../../common/layout/Navbar";

interface formDataType extends IObjectKeys {
  from: string;
  password: string;
  smtpHost: string;
  smtpPort: string;
}

export interface MasterDataProps {
  type: PageFor;
  setModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ListProps extends MasterDataProps {
  setOperation: React.Dispatch<React.SetStateAction<Operation>>;
  setForm?: any;
}
export interface AddMasterDataProps extends ListProps {
  formData: formDataType;
  handleValueChange: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => void;
  meta: FormikProps<formDataType>;
}

const EmailConfig = ({ type, setModal }: MasterDataProps) => {
  const navigate = useNavigate();
  
  const { customer_id } = useUrlParams();
  const { user_details } = useLoggedInUserDetails();
  const { state } = useContext(AppContext);

  const [operation, setOperation] = useState(Operation.CREATE);
  const [message, setMessage] = useState(emptyMessage);
  const [formData, setFormData] = useState<formDataType>({
    from: "",
    password: "",
    smtpHost: "",
    smtpPort: "",
  });

  const [addEmailConfig, { loading: AddEmailConfigLoading }] = useMutation<
    AddEmailConfigResult,
    AddEmailConfigVar
  >(AddEmailConfig, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [updateEmailConfig, { loading: UpdateEmailConfigLoading }] =
    useMutation<UpdateEmailConfigVar, UpdateEmailConfigResult>(
      UpdateEmailConfig,
      {
        onError: (e) => {
          setMessage({
            flag: true,
            message: e.message,
            operation: Operation.NONE,
          });
        },
      }
    );

  const handleSubmit = async () => {
    if (customer_id) {
      if (operation === Operation.CREATE) {
        addEmailConfig({
          variables: {
            
            customerId: customer_id,
            user_details,
            input: formData,
          },
          refetchQueries: [
            {
              query: GetEmailConfigs,
              variables: {
                
                customerId: customer_id,
              },
            },
          ],
        }).then(({ data }) => {
          if (data) {
            setMessage({
              message: "Email config added successfully",
              flag: true,
              operation: Operation.CREATE,
            });
            handleClear();
          }
        });
      } else {
        updateEmailConfig({
          variables: {
            
            emailConfigId: state.emailConfigId,
            user_details,
            input: formData,
          },
          refetchQueries: [
            {
              query: GetEmailConfigs,
              variables: {
                
                customerId: customer_id,
              },
            },
          ],
        }).then(({ data }) => {
          if (data) {
            setMessage({
              message: "Email config updated successfully",
              flag: true,
              operation: Operation.UPDATE,
            });
          }
        });
      }
    }
  };

  const handleClear = () => {
    const inputElement = document.getElementsByName(
      "from"
    )[0] as HTMLInputElement;
    if (inputElement) inputElement.focus();
    setFormData({
      from: "",
      password: "",
      smtpHost: "",
      smtpPort: "",
    });
  };
  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      handleClear();
    }
    setMessage(emptyMessage);
  };

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData(() => ({
      ...formData,
      [name]: value,
    }));
    meta.handleChange(e);
  };

  return (
    <>
      {type === PageFor.GENERAL ? (
        <>
          <Navbar NavType={LicenseTypes.CONFIG} />
          <Title>Email Config</Title>
        </>
      ) : (
        <div className="master-data--modal--title">
          <Title>Email Config</Title>
          <img src={Close} alt="" onClick={() => setModal?.(false)} />
        </div>
      )}

      <Formik
        initialValues={formData}
        validationSchema={emailConfigValidation}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
      >
        {(meta) => {
          return (
            <Form
              className={`master-data${
                type === PageFor.MODAL ? "--modal" : ""
              }`}
            >
              <div
                className={`master-data__datablock${
                  type === PageFor.MODAL ? "--modal" : ""
                }`}
              >
                <div className="master-data__datablock--left">
                  <Add
                    type={PageFor.GENERAL}
                    formData={formData}
                    handleValueChange={handleValueChange}
                    setOperation={setOperation}
                    meta={meta}
                  />
                </div>
                <div className="master-data__datablock--right">
                  <List
                    type={PageFor.GENERAL}
                    setOperation={setOperation}
                    setForm={setFormData}
                  />
                </div>
              </div>
              <Button mode="save" className="sub" type="submit" />
              <Button mode="clear" type="button" onClick={handleClear} />
              {type === PageFor.GENERAL ? (
                <Button
                  mode="back"
                  type="button"
                  onClick={() => navigate(-1)}
                />
              ) : (
                <Button mode="cancel" onClick={() => setModal?.(false)} />
              )}
            </Form>
          );
        }}
      </Formik>
      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal flag={AddEmailConfigLoading || UpdateEmailConfigLoading} />
    </>
  );
};

export default EmailConfig;
